import { useState } from "react";
import { Link } from "react-router-dom";
import { Car, Bike, Check, ArrowLeft, ArrowRight, Loader2, PoundSterling, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import api, { formatApiError } from "../lib/api";
import { useContent } from "../context/ContentContext";
import HeroBackdrop from "../components/HeroBackdrop";

const STEPS = ["Pathway", "Your details", "Experience", "Review"];

const isUkPhone = (v) => {
  let n = (v || "").replace(/[\s\-()]/g, "");
  if (n.startsWith("+44")) n = "0" + n.slice(3);
  else if (n.startsWith("44")) n = "0" + n.slice(2);
  return /^0\d{9,10}$/.test(n);
};

const isDvlaCheckCode = (v) => /^[A-Za-z0-9]{8}$/.test((v || "").replace(/\s/g, ""));
const isLicenceLast8 = (v) => /^\d{3}[A-Z9]{2}\d[A-Z]{2}$/.test((v || "").replace(/\s/g, "").toUpperCase());

const empty = {
  pathway: "", first_name: "", surname: "", name: "", email: "", phone: "",
  address_line1: "", address_line2: "", town: "", county: "", postcode: "",
  nok_name: "", nok_phone: "", preferred_days: [],
  dvla_check_code: "", licence_number: "",
  experience: "", grades: [], has_grade: "", grade_body: "", grade_level: "", grade_other: "",
  adi: "No", hear_about: "", message: "",
};

export default function Join() {
  const { t } = useContent();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [pcLoading, setPcLoading] = useState(false);
  const [pcMsg, setPcMsg] = useState("");

  const lookupPostcode = async () => {
    const pc = form.postcode.trim();
    if (!pc) { setPcMsg("Enter a postcode first."); return; }
    setPcLoading(true); setPcMsg("");
    try {
      const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(pc)}`);
      const j = await res.json();
      if (j.status === 200 && j.result) {
        setForm((f) => ({ ...f, town: j.result.admin_district || j.result.parish || f.town, county: j.result.admin_county || j.result.region || f.county }));
        setPcMsg("Postcode found — please add your house number and street below.");
      } else {
        setPcMsg("Postcode not found — please check it, or type your address manually.");
      }
    } catch { setPcMsg("Couldn't look up postcode — please type your address manually."); }
    finally { setPcLoading(false); }
  };

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validateStep = () => {
    const e = {};
    if (step === 0 && !form.pathway) e.pathway = "Please choose a pathway.";
    if (step === 1) {
      if (!form.first_name.trim()) e.first_name = "Please enter your first name.";
      if (!form.surname.trim()) e.surname = "Please enter your surname.";
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = "Please enter a valid email.";
      if (!form.phone.trim()) e.phone = "Please enter a phone number.";
      else if (!isUkPhone(form.phone)) e.phone = "Enter a valid UK phone number (e.g. 07700 900000).";
      if (!form.postcode.trim()) e.postcode = "Please enter your postcode.";
      if (!form.address_line1.trim()) e.address_line1 = "Please enter your address.";
      if (!form.town.trim()) e.town = "Please enter your town or city.";
      if (!form.nok_name.trim()) e.nok_name = "Please enter a next-of-kin name.";
      if (!form.nok_phone.trim()) e.nok_phone = "Please enter a next-of-kin phone number.";
      else if (!isUkPhone(form.nok_phone)) e.nok_phone = "Enter a valid UK phone number (e.g. 07700 900000).";
      if (!form.dvla_check_code.trim()) e.dvla_check_code = "Please enter your DVLA check code.";
      else if (!isDvlaCheckCode(form.dvla_check_code)) e.dvla_check_code = "The check code is 8 letters/numbers (case-sensitive), exactly as shown on GOV.UK.";
      if (!form.licence_number.trim()) e.licence_number = "Please enter the last 8 of your licence.";
      else if (!isLicenceLast8(form.licence_number)) e.licence_number = "Enter the last 8 characters of your licence number (e.g. 123AB9CD).";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => validateStep() && setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const gradeText = () => {
    if (form.has_grade !== "Yes") return "None";
    const lvl = form.grade_body === "Other" ? (form.grade_other || "").trim() : form.grade_level;
    return [form.grade_body, lvl].filter(Boolean).join(" ") || "None";
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/applications", {
        ...form, name: `${form.first_name} ${form.surname}`.trim(),
        current_grade: gradeText(),
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="join-page">
      <section className="relative overflow-hidden bg-[#0F2744] py-14">
        <HeroBackdrop sectionKey="join" image={t("img.join", "https://images.unsplash.com/photo-1664463760781-f159dfe3af30?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600")} />
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="eyebrow">Membership application</span>
          <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold text-white">{t("join.title", "Join the club")}</h1>
          <p className="mt-3 text-slate-300">{t("join.subtitle", "It's £65 to join, which covers your first year's club membership. There's nothing to pay now — we'll be in touch about your first session and how to pay. A contribution towards your tutor's fuel is agreed at your first session, and once you pass there's a yearly RoSPA fee too.")}</p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {submitted ? (
          <div className="rounded-2xl bg-white border border-slate-200 p-8 text-center" data-testid="join-success">
            <div className="mx-auto mb-5 h-16 w-16 rounded-full bg-green-100 flex items-center justify-center"><Check className="h-8 w-8 text-green-600" /></div>
            <h2 className="text-2xl font-extrabold text-[#0F2744]">Application received</h2>
            <p className="mt-3 text-slate-600 max-w-md mx-auto">Thank you, {form.first_name || "and welcome"}. We've got your details and sent a confirmation to <strong>{form.email}</strong>.</p>
            <p className="mt-2 text-slate-600 max-w-md mx-auto">We'll be in touch about your first session and how to pay — there's nothing to pay right now.</p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <Button asChild className="bg-[#0F2744] hover:bg-[#0B192C]" data-testid="join-success-home"><Link to="/">Back to home</Link></Button>
            </div>
          </div>
        ) : (
        <>
        {/* progress */}
        <div className="mb-8">
          <div className="flex justify-between mb-2 text-sm font-medium">
            {STEPS.map((s, i) => (
              <span key={s} className={i <= step ? "text-[#0F2744]" : "text-slate-400"} data-testid={`step-label-${i}`}>{s}</span>
            ))}
          </div>
          <Progress value={((step + 1) / STEPS.length) * 100} className="h-2" data-testid="join-progress" />
        </div>

        <div key={step} className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 step-fade">
          {step === 0 && (
            <div data-testid="step-pathway">
              <h2 className="text-xl font-bold text-[#0F2744] mb-1">Which pathway interests you?</h2>
              <p className="text-slate-500 mb-6">You can always change or do both later.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { k: "rider", label: "Advanced Rider", icon: Bike, accent: "#F59E0B" },
                  { k: "driver", label: "Advanced Driver", icon: Car, accent: "#2563EB" },
                ].map((o) => (
                  <button
                    key={o.k}
                    onClick={() => set("pathway", o.k)}
                    data-testid={`pathway-option-${o.k}`}
                    className={`relative rounded-2xl border-2 p-6 text-left transition-all ${form.pathway === o.k ? "border-[#0F2744] bg-slate-50" : "border-slate-200 hover:border-slate-300"}`}
                  >
                    {form.pathway === o.k && <div className="absolute top-3 right-3 h-6 w-6 rounded-full bg-[#0F2744] flex items-center justify-center"><Check className="h-4 w-4 text-white" /></div>}
                    <div className="h-12 w-12 rounded-xl flex items-center justify-center mb-3" style={{ background: o.accent }}><o.icon className="h-7 w-7 text-white" /></div>
                    <div className="font-bold text-[#0F2744] text-lg">{o.label}</div>
                  </button>
                ))}
              </div>
              {errors.pathway && <p className="mt-3 text-sm text-red-600" data-testid="error-pathway">{errors.pathway}</p>}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5" data-testid="step-details">
              <h2 className="text-xl font-bold text-[#0F2744]">Your details</h2>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="First name *" error={errors.first_name}>
                  <Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} placeholder="Jane" data-testid="input-first-name" />
                </Field>
                <Field label="Surname *" error={errors.surname}>
                  <Input value={form.surname} onChange={(e) => set("surname", e.target.value)} placeholder="Smith" data-testid="input-surname" />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Email address *" error={errors.email}>
                  <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@example.com" data-testid="input-email" />
                </Field>
                <Field label="Phone number *" error={errors.phone}>
                  <Input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="07700 900000" data-testid="input-phone" />
                </Field>
              </div>

              <div className="rounded-xl border border-slate-200 p-4 space-y-4">
                <div className="font-semibold text-[#0F2744]">Address *</div>
                <div className="flex gap-2 items-end">
                  <div className="flex-1"><Field label="Postcode" error={errors.postcode}>
                    <Input value={form.postcode} onChange={(e) => set("postcode", e.target.value.toUpperCase())} placeholder="B1 1AA" data-testid="input-postcode" />
                  </Field></div>
                  <Button type="button" variant="outline" onClick={lookupPostcode} disabled={pcLoading} data-testid="postcode-lookup-btn">
                    {pcLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Find address"}
                  </Button>
                </div>
                {pcMsg && <p className="text-xs text-slate-500" data-testid="postcode-msg">{pcMsg}</p>}
                <Field label="Address line 1" error={errors.address_line1}>
                  <Input value={form.address_line1} onChange={(e) => set("address_line1", e.target.value)} placeholder="House number & street" data-testid="input-address1" />
                </Field>
                <Field label="Address line 2 (optional)">
                  <Input value={form.address_line2} onChange={(e) => set("address_line2", e.target.value)} data-testid="input-address2" />
                </Field>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Town / City" error={errors.town}>
                    <Input value={form.town} onChange={(e) => set("town", e.target.value)} data-testid="input-town" />
                  </Field>
                  <Field label="County (optional)">
                    <Input value={form.county} onChange={(e) => set("county", e.target.value)} data-testid="input-county" />
                  </Field>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Next of kin — name *" error={errors.nok_name}>
                  <Input value={form.nok_name} onChange={(e) => set("nok_name", e.target.value)} data-testid="input-nok-name" />
                </Field>
                <Field label="Next of kin — phone *" error={errors.nok_phone}>
                  <Input type="tel" value={form.nok_phone} onChange={(e) => set("nok_phone", e.target.value)} data-testid="input-nok-phone" />
                </Field>
              </div>

              <div className="rounded-xl bg-blue-50 border border-blue-100 p-5">
                <div className="font-semibold text-[#0F2744]">Driving licence check (required)</div>
                <p className="text-sm text-slate-600 mt-1">A DVLA <strong>check code</strong> lets us confirm your licence, categories and any endorsements. Here's how:</p>
                <ol className="mt-2 text-sm text-slate-600 list-decimal list-inside space-y-1">
                  <li>Open the DVLA "Share your licence" service (button below).</li>
                  <li>Sign in with your <strong>driving licence number</strong>, <strong>National Insurance number</strong> and <strong>postcode</strong>.</li>
                  <li>Choose "Get a check code" and copy the 8-character code (e.g. <span className="font-mono">Ab12Cd3E</span>) — it's case-sensitive and valid for 21 days.</li>
                  <li>Paste the code and the last 8 characters of your licence number below.</li>
                </ol>
                <a href="https://www.gov.uk/view-driving-licence" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-blue-700 hover:text-blue-800" data-testid="dvla-link">Get my DVLA check code on GOV.UK <ArrowRight className="h-4 w-4" /></a>
                <div className="grid sm:grid-cols-2 gap-4 mt-4">
                  <div>
                    <Field label="DVLA check code" error={errors.dvla_check_code}>
                      <Input value={form.dvla_check_code} onChange={(e) => set("dvla_check_code", e.target.value)} placeholder="Ab12Cd3E" maxLength={10} data-testid="input-dvla" />
                    </Field>
                    <p className="text-xs text-slate-400 mt-1">8 letters/numbers, case-sensitive — enter it exactly as GOV.UK shows it.</p>
                  </div>
                  <div>
                    <Field label="Last 8 of licence number" error={errors.licence_number}>
                      <Input value={form.licence_number} onChange={(e) => set("licence_number", e.target.value.toUpperCase())} placeholder="e.g. 123AB9CD" maxLength={8} data-testid="input-licence" />
                    </Field>
                    <p className="text-xs text-slate-400 mt-1">The last 8 characters of your 16-character licence number.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="step-experience">
              <h2 className="text-xl font-bold text-[#0F2744]">Your experience</h2>
              <Field label="Tell us about your current driving/riding experience">
                <Textarea rows={4} value={form.experience} onChange={(e) => set("experience", e.target.value)} placeholder="e.g. Passed my test 5 years ago, returning after a break..." data-testid="input-experience" />
              </Field>
              <div>
                <Label className="mb-2 block">Do you hold a current advanced grade?</Label>
                <div className="flex gap-3" data-testid="grade-select">
                  {["Yes", "No"].map((v) => (
                    <button type="button" key={v} onClick={() => set("has_grade", v)} data-testid={`has-grade-${v.toLowerCase()}`}
                      className={`rounded-xl border px-5 py-2.5 text-sm font-semibold transition-colors ${form.has_grade === v ? "border-[#0F2744] bg-slate-50 text-[#0F2744]" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>{v}</button>
                  ))}
                </div>
                {form.has_grade === "Yes" && (
                  <div className="mt-4 space-y-4">
                    <div>
                      <Label className="mb-2 block text-sm">Which scheme?</Label>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {["IAM", "RoSPA", "BMF", "Other"].map((b) => (
                          <button type="button" key={b} onClick={() => { set("grade_body", b); set("grade_level", ""); }} data-testid={`grade-body-${b.toLowerCase()}`}
                            className={`rounded-xl border p-3 text-sm font-semibold transition-colors ${form.grade_body === b ? "border-[#0F2744] bg-slate-50 text-[#0F2744]" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>{b}</button>
                        ))}
                      </div>
                    </div>
                    {form.grade_body && form.grade_body !== "Other" && (
                      <div>
                        <Label className="mb-2 block text-sm">Grade</Label>
                        <div className="flex flex-wrap gap-3">
                          {({ IAM: ["Pass", "F1RST"], RoSPA: ["Bronze", "Silver", "Gold"], BMF: ["Pass", "Silver", "Gold"] }[form.grade_body] || []).map((g) => (
                            <button type="button" key={g} onClick={() => set("grade_level", g)} data-testid={`grade-level-${g.toLowerCase()}`}
                              className={`rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors ${form.grade_level === g ? "border-[#0F2744] bg-slate-50 text-[#0F2744]" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>{g}</button>
                          ))}
                        </div>
                      </div>
                    )}
                    {form.grade_body === "Other" && (
                      <Field label="Please tell us your advanced grade">
                        <Input value={form.grade_other} onChange={(e) => set("grade_other", e.target.value)} placeholder="e.g. Police Class 1" data-testid="input-grade-other" />
                      </Field>
                    )}
                  </div>
                )}
              </div>
              <div>
                <Label className="mb-2 block">When are you likely to be available for training?</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="preferred-days">
                  {["Saturday", "Sunday", "Weekdays", "Weekday evenings*"].map((d) => {
                    const on = form.preferred_days.includes(d);
                    return (
                      <button type="button" key={d} onClick={() => set("preferred_days", on ? form.preferred_days.filter((x) => x !== d) : [...form.preferred_days, d])}
                        className={`rounded-xl border p-3 text-sm font-semibold transition-colors ${on ? "border-[#0F2744] bg-slate-50 text-[#0F2744]" : "border-slate-200 text-slate-500 hover:border-slate-300"}`} data-testid={`day-${d.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}>
                        {d}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-1.5 text-xs text-slate-400">* light permitting</p>
              </div>
              {form.pathway === "driver" && (
                <div>
                  <Label className="mb-2 block">Are you an Approved Driving Instructor?</Label>
                  <Select value={form.adi} onValueChange={(v) => set("adi", v)}>
                    <SelectTrigger data-testid="select-adi"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="No">No</SelectItem>
                      <SelectItem value="ADI">ADI</SelectItem>
                      <SelectItem value="PDI">PDI</SelectItem>
                      <SelectItem value="Fleet">Fleet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Field label="How did you hear about us? (optional)">
                <Input value={form.hear_about} onChange={(e) => set("hear_about", e.target.value)} placeholder="Friend, web search, event..." data-testid="input-hear" />
              </Field>
              <Field label="Anything else you'd like us to know? (optional)">
                <Textarea rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="input-message" />
              </Field>
            </div>
          )}

          {step === 3 && (
            <div data-testid="step-review">
              <h2 className="text-xl font-bold text-[#0F2744] mb-4">Review & submit</h2>
              <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="join-fee-summary">
                <div className="flex items-center gap-2 font-bold text-[#0F2744]"><PoundSterling className="h-5 w-5 text-amber-600" /> Membership fees</div>
                <div className="mt-3 space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-slate-600">Joining fee (covers your first year's membership)</span><span className="font-semibold text-[#0F2744]">{t("fee.join", "£65")}</span></div>
                </div>
                {form.pathway === "rider" && <p className="mt-3 text-xs text-slate-600">Your joining fee includes a copy of <strong>Roadcraft</strong> and the <strong>Highway Code</strong>.</p>}
                <p className="mt-2 text-xs text-slate-600">Annual club membership is due each April. A small contribution towards your tutor's fuel is agreed at your first session. After you pass your test, a yearly RoSPA fee also applies.</p>
                <p className="mt-2 text-xs text-slate-500 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-green-600" /> No payment is taken now — we'll be in touch about your first session and how to pay.</p>
              </div>
              <dl className="space-y-3">
                {[
                  ["Pathway", form.pathway === "driver" ? "Advanced Driver" : form.pathway === "rider" ? "Advanced Rider" : "—"],
                  ["Name", `${form.first_name} ${form.surname}`.trim() || "—"],
                  ["Email", form.email],
                  ["Phone", form.phone],
                  ["Address", [form.address_line1, form.address_line2, form.town, form.county, form.postcode].filter(Boolean).join(", ") || "—"],
                  ["Next of kin", [form.nok_name, form.nok_phone].filter(Boolean).join(" · ") || "—"],
                  ["Availability", form.preferred_days.join(", ") || "—"],
                  ["DVLA check code", form.dvla_check_code || "—"],
                  ["Advanced grade", gradeText()],
                  ...(form.pathway === "driver" ? [["Approved Driving Instructor", form.adi]] : []),
                  ["Experience", form.experience || "—"],
                ].map(([k, v]) => (
                  <div key={k} className="flex flex-col sm:flex-row sm:justify-between gap-1 py-2 border-b border-slate-100" data-testid={`review-${k.toLowerCase()}`}>
                    <dt className="text-sm font-semibold text-slate-500">{k}</dt>
                    <dd className="text-slate-800 sm:text-right sm:max-w-md">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between gap-3">
            {step > 0 ? (
              <Button variant="outline" onClick={back} data-testid="join-back-btn"><ArrowLeft className="h-4 w-4 mr-1.5" /> Back</Button>
            ) : <span />}
            {step < STEPS.length - 1 ? (
              <Button onClick={next} className="bg-[#0F2744] hover:bg-[#0B192C]" data-testid="join-next-btn">Continue <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
            ) : (
              <Button onClick={submit} disabled={submitting} className="bg-amber-500 hover:bg-amber-600 text-[#0F2744] font-bold" data-testid="join-submit-btn">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit application"}
              </Button>
            )}
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <div>
      <Label className="mb-1.5 block text-slate-700">{label}</Label>
      {children}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}
